/* R&R custom cart bridge v3 — stable cart controls + R&R case-gift handoff. */
(() => {
  if (window.__rrCartBridgeV3Loaded) return;
  window.__rrCartBridgeV3Loaded = true;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  const money = (cents, currency = 'USD') => {
    try { return new Intl.NumberFormat(undefined,{style:'currency',currency}).format((Number(cents)||0)/100); }
    catch { return `$${((Number(cents)||0)/100).toFixed(2)}`; }
  };
  let lastFocus = null;
  let mutationInFlight = false;

  async function fetchJson(url, options = {}, timeoutMs = 12000) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url,{credentials:'same-origin',cache:'no-store',...options,signal:controller.signal});
      let payload = null;
      try { payload = await response.json(); } catch {}
      return {response,payload};
    } finally { clearTimeout(timeout); }
  }

  async function getCart() {
    const {response,payload} = await fetchJson(`${rootPath()}cart.js`,{headers:{Accept:'application/json'}});
    if (!response.ok || !payload) throw new Error('We couldn’t load your bag. Try again.');
    return payload;
  }

  function syncBagCount(cart) {
    const count = Number(cart?.item_count) || 0;
    document.querySelectorAll('[data-rr-bag-count]').forEach(el => { el.textContent = String(count); });
  }

  function feedbackFor(form) {
    let node = form.querySelector('[data-rr-cart-feedback]');
    if (node) return node;
    node = document.createElement('p');
    node.className = 'rr-cart-feedback';
    node.dataset.rrCartFeedback = '';
    node.setAttribute('role','status');
    node.setAttribute('aria-live','polite');
    form.append(node);
    return node;
  }

  function ensureSheet() {
    let shell = document.getElementById('rr-cart-sheet');
    if (shell) return shell;
    shell = document.createElement('div');
    shell.id = 'rr-cart-sheet';
    shell.className = 'rr-cart-sheet';
    shell.setAttribute('aria-hidden','true');
    shell.innerHTML = `
      <div class="rr-cart-sheet__overlay" data-rr-sheet-close></div>
      <section class="rr-cart-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="rr-cart-sheet-title">
        <div class="rr-cart-sheet__handle" aria-hidden="true"></div>
        <header class="rr-cart-sheet__head">
          <div><p class="rr-cart-sheet__eyebrow">YOUR BAG</p><h2 id="rr-cart-sheet-title">Added. Keep going.</h2></div>
          <button type="button" class="rr-cart-sheet__close" data-rr-sheet-close aria-label="Close bag">×</button>
        </header>
        <div class="rr-cart-sheet__body" data-rr-sheet-body></div>
        <footer class="rr-cart-sheet__foot">
          <div class="rr-cart-sheet__total"><span>Subtotal</span><strong data-rr-sheet-total></strong></div>
          <p class="rr-cart-sheet__saving" data-rr-sheet-saving hidden></p>
          <div class="rr-cart-sheet__actions"><button type="button" class="rr-cart-sheet__keep" data-rr-sheet-close>Keep shopping</button><a class="rr-cart-sheet__checkout" href="/checkout">Checkout</a></div>
          <a class="rr-cart-sheet__full" href="/cart">View full bag</a>
        </footer>
      </section>`;
    document.body.append(shell);

    shell.addEventListener('click', event => {
      if (event.target.closest('[data-rr-sheet-close]')) { closeSheet(); return; }
      if (mutationInFlight) return;
      const qty = event.target.closest('[data-rr-qty-delta]');
      if (qty) {
        const delta = Number(qty.dataset.rrQtyDelta);
        if (Number.isFinite(delta) && delta !== 0) changeLine({key:qty.dataset.key,variantId:qty.dataset.variantId,delta});
        return;
      }
      const remove = event.target.closest('[data-rr-remove]');
      if (remove) changeLine({key:remove.dataset.key,variantId:remove.dataset.variantId,remove:true});
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && shell.classList.contains('is-open')) closeSheet();
    });
    return shell;
  }

  function closeSheet() {
    const shell = document.getElementById('rr-cart-sheet');
    if (!shell) return;
    shell.classList.remove('is-open','is-loading');
    shell.setAttribute('aria-hidden','true');
    shell.removeAttribute('aria-busy');
    document.body.classList.remove('rr-cart-sheet-open');
    lastFocus?.focus?.({preventScroll:true});
  }

  function clearSheetError() { ensureSheet().querySelector('[data-rr-sheet-error]')?.remove(); }
  function showSheetError(message) {
    const shell = ensureSheet();
    const body = shell.querySelector('[data-rr-sheet-body]');
    let note = shell.querySelector('[data-rr-sheet-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'rr-cart-sheet__error';
      note.dataset.rrSheetError = '';
      note.setAttribute('role','alert');
      body.prepend(note);
    }
    note.textContent = message || 'We couldn’t update your bag. Try again.';
  }

  function setSheetBusy(busy) {
    mutationInFlight = !!busy;
    const shell = ensureSheet();
    shell.classList.toggle('is-loading',!!busy);
    if (busy) shell.setAttribute('aria-busy','true'); else shell.removeAttribute('aria-busy');
    shell.querySelectorAll('[data-rr-qty-delta],[data-rr-remove]').forEach(control => {
      control.disabled = !!busy;
      control.setAttribute('aria-disabled',String(!!busy));
    });
  }

  function lineRow(item, currency) {
    const row = document.createElement('article');
    row.className = 'rr-cart-sheet__item';
    row.dataset.lineKey = item.key || '';

    const media = document.createElement('div');
    media.className = 'rr-cart-sheet__media';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = item.product_title || '';
      img.loading = 'eager';
      media.append(img);
    }

    const info = document.createElement('div');
    info.className = 'rr-cart-sheet__info';
    const title = document.createElement('a');
    title.href = item.url || '/cart';
    title.className = 'rr-cart-sheet__title';
    title.textContent = item.product_title || item.title || 'Item';
    info.append(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'rr-cart-sheet__variant';
      variant.textContent = item.variant_title;
      info.append(variant);
    }

    const price = document.createElement('p');
    price.className = 'rr-cart-sheet__price';
    price.textContent = money(item.final_line_price,currency);
    info.append(price);

    const controls = document.createElement('div');
    controls.className = 'rr-cart-sheet__controls';
    const variantId = String(item.variant_id || item.id || '');
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'rr-cart-sheet__qty-button rr-cart-sheet__qty-button--minus';
    minus.textContent = '−';
    minus.dataset.rrQtyDelta = '-1';
    minus.dataset.key = item.key || '';
    minus.dataset.variantId = variantId;
    minus.setAttribute('aria-label',`Decrease ${title.textContent} quantity`);

    const q = document.createElement('span');
    q.className = 'rr-cart-sheet__quantity';
    q.textContent = String(item.quantity);
    q.setAttribute('aria-label',`Quantity ${item.quantity}`);

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'rr-cart-sheet__qty-button rr-cart-sheet__qty-button--plus';
    plus.textContent = '+';
    plus.dataset.rrQtyDelta = '1';
    plus.dataset.key = item.key || '';
    plus.dataset.variantId = variantId;
    plus.setAttribute('aria-label',`Increase ${title.textContent} quantity`);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'rr-cart-sheet__remove';
    remove.textContent = 'Remove';
    remove.dataset.key = item.key || '';
    remove.dataset.variantId = variantId;
    remove.setAttribute('data-rr-remove','');
    remove.setAttribute('aria-label',`Remove ${title.textContent} from bag`);

    controls.append(minus,q,plus,remove);
    info.append(controls);
    row.append(media,info);
    return row;
  }

  function renderCart(cart) {
    const shell = ensureSheet();
    const body = shell.querySelector('[data-rr-sheet-body]');
    const total = shell.querySelector('[data-rr-sheet-total]');
    const saving = shell.querySelector('[data-rr-sheet-saving]');
    const checkout = shell.querySelector('.rr-cart-sheet__checkout');
    body.replaceChildren();
    const currency = cart.currency || 'USD';

    if (!cart.items?.length) {
      const empty = document.createElement('div');
      empty.className = 'rr-cart-sheet__empty';
      empty.innerHTML = '<p>Your bag is empty.</p><span>Keep browsing and come back when you find your one.</span>';
      body.append(empty);
      checkout.setAttribute('aria-disabled','true');
      checkout.tabIndex = -1;
    } else {
      cart.items.forEach(item => body.append(lineRow(item,currency)));
      const gift = document.createElement('div');
      gift.className = 'rr-cart-gift-summary';
      gift.dataset.rrGiftSummary = '';
      body.append(gift);
      checkout.removeAttribute('aria-disabled');
      checkout.removeAttribute('tabindex');
    }

    total.textContent = money(cart.total_price,currency);
    const saved = Math.max(0,(Number(cart.original_total_price)||0)-(Number(cart.total_price)||0));
    saving.hidden = saved <= 0;
    saving.textContent = saved > 0 ? `You’re saving ${money(saved,currency)} on this bag.` : '';
    syncBagCount(cart);
    document.dispatchEvent(new CustomEvent('rr:cart-rendered',{detail:{cart}}));
  }

  async function openSheet(cart) {
    const shell = ensureSheet();
    renderCart(cart || await getCart());
    clearSheetError();
    lastFocus = document.activeElement;
    shell.classList.add('is-open');
    shell.setAttribute('aria-hidden','false');
    document.body.classList.add('rr-cart-sheet-open');
    requestAnimationFrame(() => shell.querySelector('[data-rr-sheet-close]')?.focus());
  }

  function findCurrentLine(cart,key,variantId) {
    if (!cart?.items?.length) return null;
    if (key) {
      const exact = cart.items.find(item => item.key === key);
      if (exact) return exact;
    }
    if (variantId) {
      const matches = cart.items.filter(item => String(item.variant_id || item.id || '') === String(variantId));
      if (matches.length === 1) return matches[0];
    }
    return null;
  }

  async function handoffGift(cart) {
    try {
      if (!window.RRCaseGift?.afterCartMutation) return false;
      return !!(await window.RRCaseGift.afterCartMutation(cart));
    } catch { return false; }
  }

  async function changeLine({key,variantId,delta=0,remove=false}) {
    if (mutationInFlight) return;
    setSheetBusy(true);
    clearSheetError();
    try {
      const before = await getCart();
      const current = findCurrentLine(before,key,variantId);
      if (!current) { renderCart(before); return; }
      const quantity = remove ? 0 : Math.max(0,Number(current.quantity||0)+Number(delta||0));
      if (!remove && quantity === Number(current.quantity||0)) { renderCart(before); return; }

      const {response,payload} = await fetchJson(`${rootPath()}cart/change.js`,{
        method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id:current.key,quantity})
      });
      if (!response.ok) throw new Error(payload?.description || 'We couldn’t update your bag. Try again.');
      await sleep(120);
      const latest = await getCart();
      renderCart(latest);
      clearSheetError();
      const giftOpened = await handoffGift(latest);
      document.dispatchEvent(new CustomEvent('rr:cart-updated',{detail:{cart:latest,source:'cart-bridge'}}));
      if (giftOpened) closeSheet();
    } catch (error) {
      try { renderCart(await getCart()); } catch {}
      showSheetError(error?.name === 'AbortError' ? 'The bag update took too long. Try again.' : (error?.message || 'We couldn’t update your bag. Try again.'));
    } finally { setSheetBusy(false); }
  }

  document.addEventListener('rr:gift-open', closeSheet);

  document.addEventListener('click', event => {
    const bag = event.target.closest?.('[data-rr-bag-link]');
    if (!bag) return;
    event.preventDefault();
    openSheet().catch(() => { window.location.href = bag.href; });
  },true);

  document.addEventListener('submit', async event => {
    const form = event.target?.closest?.('.rr-product-form');
    if (!form || form.dataset.rrAjaxSubmitting === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = form.querySelector('[data-rr-buy]');
    const feedback = feedbackFor(form);
    feedback.textContent = '';
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    if (!data.get('id')) { feedback.textContent = 'Choose an option before adding to your bag.'; return; }

    const originalLabel = button?.textContent || 'Add to bag';
    form.dataset.rrAjaxSubmitting = 'true';
    form.setAttribute('aria-busy','true');
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const {response,payload} = await fetchJson(`${rootPath()}cart/add.js`,{method:'POST',headers:{Accept:'application/json'},body:data});
      if (!response.ok || payload?.status === 422) throw new Error(payload?.description || 'That item could not be added. Try again.');
      const cart = await getCart();
      syncBagCount(cart);
      const giftOpened = await handoffGift(cart);
      document.dispatchEvent(new CustomEvent('rr:cart-updated',{detail:{cart,source:'cart-bridge'}}));
      if (!giftOpened) await openSheet(cart);
    } catch (error) {
      feedback.textContent = error?.name === 'AbortError' ? 'Adding took too long. Try again.' : (error?.message || 'That item could not be added. Try again.');
    } finally {
      form.dataset.rrAjaxSubmitting = 'false';
      form.removeAttribute('aria-busy');
      if (button) { button.disabled = false; button.textContent = originalLabel; }
    }
  },true);

  window.addEventListener('pageshow', async () => {
    document.body.classList.remove('rr-cart-sheet-open');
    try {
      const cart = await getCart();
      syncBagCount(cart);
      document.dispatchEvent(new CustomEvent('rr:cart-updated',{detail:{cart,source:'cart-bridge'}}));
    } catch {}
  });
})();
